import { loadStripe } from "@stripe/stripe-js";
import { keepPreviousData } from "@tanstack/react-query";
import { differenceInCalendarDays } from "date-fns";
import { ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";
import { trpc } from "~/api/trpc";
import DiscountCodeSection from "~/components/booking-review/DiscountCodeSection";
import MessageSection from "~/components/booking-review/MessageSection";
import PaymentMethodSection from "~/components/booking-review/PaymentMethodSection";
import ScrollMoreHint from "~/components/booking-review/ScrollMoreHint";
import ServicesList from "~/components/booking-review/ServicesList";
import SummaryCard from "~/components/booking-review/SummaryCard";
import PrimaryActionButton from "~/components/new/PrimaryActionButton";
import TopProgressBar from "~/components/new/TopProgressBar";
import { useScuid } from "~/hooks/use-scuid";
import { getCartItemTotalDuration, getCartItemGtmTotalPrice, getCartTotalAfterDiscount, toGtmCartItems, useBookingFlowStore } from "~/stores/booking-flow-store";
import { useCustomerAuthStore } from "~/stores/customer-auth-store";
import { Language, useLanguageStore } from "~/stores/language-store";
import { GTMEvent } from "../../components/gtmProvider/GTMContext";
import useGTM from "../../components/gtmProvider/useGTM";
import { isEmailBlockedFromBooking } from "../../helper/common/blockedEmails";
import toFixedNumber from "../../helper/common/toFixedNumber";
import { KLARNA_MIN_CART_TOTAL_EUR } from "~/lib/klarna";
import { isEmbedded, postToParent, rememberEmbedReturnPage, takeDrawerHandover } from "~/lib/embed";


const KLARNA_BILLING_COUNTRY = 'DE';


const FixedBottomBar = () => {
    const {
        selectedStore,
        selectedTimeslot,
        cartItems,
        firstName,
        lastName,
        email,
        phoneDialCode,
        phone,
        messageFromCustomer,
        discountCode,
        wantsEarlierTimeslot,
        preferredContactTimes,
        receiveNewsletter,
        deeplinkPractitionerId,
        preferredPractitionerId,
        paymentMethod,
    } = useBookingFlowStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
            selectedTimeslot: state.selectedTimeslot,
            cartItems: state.cartItems,
            firstName: state.firstName,
            lastName: state.lastName,
            email: state.email,
            phoneDialCode: state.phoneDialCode,
            phone: state.phone,
            messageFromCustomer: state.messageFromCustomer,
            discountCode: state.discountCode,
            wantsEarlierTimeslot: state.wantsEarlierTimeslot,
            preferredContactTimes: state.preferredContactTimes,
            receiveNewsletter: state.receiveNewsletter,
            deeplinkPractitionerId: state.deeplinkPractitionerId,
            preferredPractitionerId: state.preferredPractitionerId,
            paymentMethod: state.paymentMethod,
        }))
    );

    const bookAppointmentMutation = trpc.publicAppointments.bookAppointment.useMutation();
    const createKlarnaCheckoutMutation = trpc.publicPayments.createKlarnaCheckout.useMutation();
    const { isAuthenticated } = useCustomerAuthStore();

    const { sendEvent } = useGTM();
    const navigate = useNavigate();
    const getScuid = useScuid();

    const { t, language } = useLanguageStore();

    const [isEmailBlocked, setIsEmailBlocked] = useState(false);

    const isSubmittingClinicBookingRef = useRef(false);
    const [isSubmittingClinicBooking, setIsSubmittingClinicBooking] = useState(false);

    // CMS Config
    const { data: cmsConfigData } = trpc.publicBookingFunnel.getBookingReviewConfig.useQuery(undefined, {
        staleTime: 60000,
    });
    const cmsConfig = cmsConfigData?.config;

    const buildBookableItems = () => cartItems.map(item => ({
        bookableItemId: item.bookableItemId,
        bookableItemType: item.bookableItemType as 'SERVICE' | 'BUNDLE',
        treatmentVariantId: item.selectedVariantId,
        addOns: item.addOns.map(addon => ({
            addOnId: addon.addOnId,
            variantId: addon.variantId,
        })),
        bundleId: item.bundleId,
        isBundleRedemption: item.isBundleRedemption ?? false,
    }));

    const sendGTMEvent = (result: { appointmentId: string; isFirstBooking?: boolean }) => {
        sendEvent(GTMEvent.BOOKING_FUNNEL_BOOKED, {
            is_first_booking: result.isFirstBooking === true,
            transaction_id: result.appointmentId,
            appointment: {
                datetime: selectedTimeslot?.getDisplayDateTime(),
                notify_about_earlier_possible_appointment: false,
            },
            total_net: toFixedNumber(
                cartItems
                    .map((item) => getCartItemGtmTotalPrice(item) / 1.19)
                    .reduce((acc, netPrice) => (acc || 0) + (netPrice || 0)),
            ),
            total_gross: toFixedNumber(
                cartItems
                    .map((item) => getCartItemGtmTotalPrice(item))
                    .reduce((acc, netPrice) => (acc || 0) + (netPrice || 0)),
            ),
            coupon: discountCode,
            cart_items: toGtmCartItems(cartItems),
            branch: {
                id: selectedStore?.id,
                name: selectedStore?.name,
            },
            user: {
                id: null,
                has_account: false,
                email: email.toLowerCase(),
                phone: phone,
            },
            payment_method: 'clinic',
        });
    };

    const handleClinicPayment = async () => {
        if (!selectedTimeslot || !selectedStore) {
            toast.error(t('bookingErrorInvalidSelectedData'));
            return;
        }
        if (isSubmittingClinicBookingRef.current) {
            return;
        }
        // Set synchronously so a double click can't start a second booking while the scuid is being resolved.
        isSubmittingClinicBookingRef.current = true;
        setIsSubmittingClinicBooking(true);

        try {
            const constrainedPractitionerId = deeplinkPractitionerId ?? (isAuthenticated() ? preferredPractitionerId : null);
            const result = await bookAppointmentMutation.mutateAsync({
                storeId: selectedStore.id ?? -1,
                selectedStartTime: selectedTimeslot.toJson(),
                bookableItems: buildBookableItems(),
                email: email.trim().toLowerCase(),
                firstName,
                lastName,
                phone: phoneDialCode + phone.replaceAll(" ", ""),
                couponCodes: discountCode,
                wantsEarlierTimeslot,
                preferredContactTime: preferredContactTimes.length === 0 ? 'No Call-Time Preference' : preferredContactTimes.sort().join(", "),
                receiveNewsletter: receiveNewsletter !== false,
                message: messageFromCustomer,
                scuid: await getScuid(),
                ...(constrainedPractitionerId ? { practitionerId: constrainedPractitionerId } : {}),
                ...(isAuthenticated() && preferredPractitionerId && !deeplinkPractitionerId ? { preferredPractitionerId } : {}),
            });

            sendGTMEvent(result);
            navigate("/confirmation", { state: { selectedStore } });
        } finally {
            isSubmittingClinicBookingRef.current = false;
            setIsSubmittingClinicBooking(false);
        }
    };

    const handleKlarnaPayment = async () => {
        if (!selectedTimeslot || !selectedStore) {
            toast.error(t('bookingErrorInvalidSelectedData'));
            return;
        }

        const returnUrl = `${window.location.origin}/klarna-return`;

        const result = await createKlarnaCheckoutMutation.mutateAsync({
            storeId: selectedStore.id ?? -1,
            selectedStartTime: selectedTimeslot.toJson(),
            bookableItems: buildBookableItems(),
            email: email.trim().toLowerCase(),
            firstName,
            lastName,
            phone: phoneDialCode + phone.replaceAll(" ", ""),
            couponCodes: discountCode,
            message: messageFromCustomer,
            wantsEarlierTimeslot,
            preferredContactTime: preferredContactTimes.length === 0 ? 'No Call-Time Preference' : preferredContactTimes.sort().join(", "),
            receiveNewsletter: receiveNewsletter !== false,
            returnUrl,
            language: language === Language.GERMAN ? 'de' : 'en',
            scuid: await getScuid(),
        });

        if (!result.success) {
            toast.error(result.error || (language === Language.GERMAN ? 'Klarna-Zahlung konnte nicht gestartet werden.' : 'Could not start Klarna payment.'));
            return;
        }

        const stripe = await loadStripe(result.publishableKey);
        if (!stripe) {
            toast.error(language === Language.GERMAN ? 'Zahlung konnte nicht initialisiert werden.' : 'Could not initialize payment.');
            return;
        }

        // Klarna refuses to be framed, so when embedded the parent page has to perform the redirect.
        const embedded = isEmbedded();
        const { error, paymentIntent } = await stripe.confirmKlarnaPayment(result.clientSecret, {
            payment_method: {
                billing_details: {
                    name: `${firstName} ${lastName}`.trim(),
                    email: email.trim().toLowerCase(),
                    phone: phoneDialCode + phone.replaceAll(" ", ""),
                    address: { country: KLARNA_BILLING_COUNTRY },
                },
            },
            return_url: `${returnUrl}?klarna_auth_id=${result.klarnaAuthorizationId}`,
        }, { handleActions: !embedded });

        if (error) {
            toast.error(error.message || (language === Language.GERMAN ? 'Klarna-Zahlung fehlgeschlagen.' : 'Klarna payment failed.'));
            return;
        }

        if (embedded) {
            const redirectUrl = paymentIntent?.next_action?.redirect_to_url?.url;
            if (!redirectUrl) {
                toast.error(language === Language.GERMAN ? 'Klarna-Zahlung konnte nicht gestartet werden.' : 'Could not start Klarna payment.');
                return;
            }
            rememberEmbedReturnPage();
            postToParent("redirect", { url: redirectUrl });
        }
    };

    const handleClick = async () => {
        if (isEmailBlockedFromBooking(email)) {
            setIsEmailBlocked(true);
            toast.error(t('bookingErrorEmailBlocked'));
            return;
        }
        setIsEmailBlocked(false);

        try {
            if (paymentMethod === 'klarna') {
                await handleKlarnaPayment();
            } else {
                await handleClinicPayment();
            }
        } catch (e: any) {
            console.log(e);
            if (e.message === "The desired slot is not available anymore.") {
                toast.error(t('bookingErrorTheDesiredSlotIsNotAvailableAnymore'));
            } else {
                toast.error(t('bookingErrorUnknown'));
            }
        }
    };

    const isLoading = isSubmittingClinicBooking || bookAppointmentMutation.isPending || createKlarnaCheckoutMutation.isPending;

    const bookNowText = language === Language.GERMAN
        ? (cmsConfig?.bookNowButtonTextDe ?? 'JETZT BUCHEN')
        : (cmsConfig?.bookNowButtonTextEn ?? 'BOOK NOW');

    return (
        <div className="fixed z-10 bottom-0 left-0 w-full md:max-w-7xl md:left-1/2 md:-translate-x-1/2 flex flex-col gap-3 px-6 pt-4 pb-6 bg-light-burgundy">
            <ScrollMoreHint />
            {isEmailBlocked && (
                <span className="text-sm font-light text-red-600">
                    {t('bookingErrorEmailBlocked')}
                </span>
            )}
            <PrimaryActionButton
                onClick={handleClick}
                label={bookNowText}
                disabled={cartItems.length === 0}
                loading={isLoading}
                rightIcon={<ArrowRight />}
            />
        </div>
    )
}

const BookingSummaryCard = () => {
    const { cartItems, selectedStore, selectedTimeslot, firstName, lastName } = useBookingFlowStore(
        useShallow((state) => ({
            cartItems: state.cartItems,
            selectedStore: state.selectedStore,
            selectedTimeslot: state.selectedTimeslot,
            firstName: state.firstName,
            lastName: state.lastName,
        }))
    );

    if (!selectedStore) {
        return null;
    }

    const totalDurationMinutes = cartItems.reduce((current, item) => current + getCartItemTotalDuration(item), 0);
    const startTime = selectedTimeslot ?? undefined;
    const endTime = startTime?.clone().addMinutes(totalDurationMinutes);

    return (
        <SummaryCard
            storeName={selectedStore.name}
            storeAddress={[selectedStore.address, selectedStore.zipCode].filter(Boolean).join(', ')}
            storeImage={selectedStore.images?.[0]}
            startTime={startTime}
            endTime={endTime}
            customerName={`${firstName} ${lastName}`}
        />
    );
}

const ServicesSection = () => {
    const { cartItems, matchedDiscountCode, discountCode } = useBookingFlowStore(
        useShallow((state) => ({
            cartItems: state.cartItems,
            matchedDiscountCode: state.matchedDiscountCode,
            discountCode: state.discountCode,
        }))
    );

    return (
        <ServicesList cartItems={cartItems} matchedDiscountCode={matchedDiscountCode} discountCode={discountCode}>
            <BookingDiscountCodeSection />
        </ServicesList>
    );
}

const BookingMessageSection = () => {
    const { messageFromCustomer, setMessageFromCustomer } = useBookingFlowStore(
        useShallow((state) => ({
            messageFromCustomer: state.messageFromCustomer,
            setMessageFromCustomer: state.setMessageFromCustomer,
        }))
    );

    return <MessageSection value={messageFromCustomer} onChange={setMessageFromCustomer} />;
}

const BookingDiscountCodeSection = () => {
    const { email, discountCode, setDiscountCode, matchedDiscountCode, setMatchedDiscountCode, cartItems, selectedTimeslot, selectedStore } = useBookingFlowStore(
        useShallow((state) => ({
            email: state.email,
            discountCode: state.discountCode,
            setDiscountCode: state.setDiscountCode,
            matchedDiscountCode: state.matchedDiscountCode,
            setMatchedDiscountCode: state.setMatchedDiscountCode,
            cartItems: state.cartItems,
            selectedTimeslot: state.selectedTimeslot,
            selectedStore: state.selectedStore,
        }))
    );

    return (
        <DiscountCodeSection
            discountCode={discountCode}
            onDiscountCodeChange={setDiscountCode}
            matchedDiscountCode={matchedDiscountCode}
            onMatchedDiscountCodeChange={setMatchedDiscountCode}
            cartItems={cartItems}
            appointmentDate={selectedTimeslot?.toUTC()}
            customerEmail={email}
            storeId={selectedStore?.id}
        />
    );
}

const AdditionalOptionsSection = () => {
    const { language } = useLanguageStore();

    return (
        <div className='flex flex-col gap-3'>
            <span className='text-sm font-normal text-burgundy'>
                {language === Language.GERMAN ? 'Weitere Optionen' : 'Additional options'}
            </span>
            <BookingMessageSection />
        </div>
    );
}

const BookingPaymentMethodSection = () => {
    const { selectedTimeslot, paymentMethod, setPaymentMethod, cartItems, matchedDiscountCode } = useBookingFlowStore(
        useShallow((state) => ({
            selectedTimeslot: state.selectedTimeslot,
            paymentMethod: state.paymentMethod,
            setPaymentMethod: state.setPaymentMethod,
            cartItems: state.cartItems,
            matchedDiscountCode: state.matchedDiscountCode,
        }))
    );

    const isWithinDateRange = (() => {
        if (!selectedTimeslot) return false;
        const appointmentDate = selectedTimeslot.toUTC();
        const daysUntilAppointment = differenceInCalendarDays(appointmentDate, new Date());
        return daysUntilAppointment <= 27;
    })();
    const meetsMinimumTotal = getCartTotalAfterDiscount(cartItems, matchedDiscountCode) >= KLARNA_MIN_CART_TOTAL_EUR;
    const isKlarnaEligible = isWithinDateRange && meetsMinimumTotal;

    useEffect(() => {
        if (!isKlarnaEligible && paymentMethod === 'klarna') {
            setPaymentMethod('clinic');
        }
    }, [isKlarnaEligible, paymentMethod, setPaymentMethod]);

    return (
        <PaymentMethodSection
            value={paymentMethod}
            onChange={setPaymentMethod}
            isWithinDateRange={isWithinDateRange}
            meetsMinimumTotal={meetsMinimumTotal}
        />
    );
};

const BookingReview = () => {
    const {
        selectedStore,
        selectedTimeslot,
        cartItems,
        email,
        phone,
    } = useBookingFlowStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
            selectedTimeslot: state.selectedTimeslot,
            cartItems: state.cartItems,
            email: state.email,
            phone: state.phone,
        }))
    );

    const { sendEvent } = useGTM();
    const navigate = useNavigate();

    const { language } = useLanguageStore();

    // CMS Config
    const { data: cmsConfigData } = trpc.publicBookingFunnel.getBookingReviewConfig.useQuery(undefined, {
        staleTime: 60000, // 1 minute cache
    });
    const cmsConfig = cmsConfigData?.config;

    const { data: store } = trpc.publicStores.getOne.useQuery({
        id: selectedStore?.id ?? -1,
    }, {
        enabled: !!selectedStore,
        staleTime: 60 * 1000,
        refetchInterval: 60 * 1000,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
    });

    useEffect(() => {
        window.scroll(0, 0);
        sendEvent(GTMEvent.BOOKING_FUNNEL_CONTACT_DETAILS, {
            appointment: {
                datetime: selectedTimeslot?.toUTC().toISOString(),
                notify_about_earlier_possible_appointment: false,
            },
            cart_items: toGtmCartItems(cartItems),
            branch: {
                id: selectedStore?.id,
                name: selectedStore?.name,
            },
            user: {
                id: null,
                has_account: false,
                email: email.toLowerCase(),
                phone: phone,
            },
        });
    }, []);

    useEffect(() => {
        if (!selectedStore) {
            navigate("/");
        }
    }, [selectedStore]);

    // Set when the standalone funnel sends the customer back into the drawer after a cancelled Klarna payment.
    useEffect(() => {
        const errorMessage = takeDrawerHandover<{ errorMessage?: string } | null>('/review-booking')?.errorMessage;
        if (errorMessage) {
            toast.error(errorMessage);
        }
    }, []);


    return (
        <>
            <TopProgressBar onClickBackButton={() => navigate('/enter-details')} percentageFilled={100} displayString={language === Language.GERMAN ? (cmsConfig?.progressDisplayDe ?? 'DETAILS BESTÄTIGEN') : (cmsConfig?.progressDisplayEn ?? 'CONFIRM DETAILS')} color="primary" />
            <div className='min-h-screen pb-64'>
                <FixedBottomBar />

                <BookingSummaryCard />

                <div className="max-w-2xl mx-auto w-full px-6 pt-8 flex flex-col gap-10">
                    <ServicesSection />
                    <div className='-mt-4'>
                        <BookingPaymentMethodSection />
                    </div>
                    <AdditionalOptionsSection />
                </div>
            </div>
        </>
    );
};

export default BookingReview;
